import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Lightbulb, CheckCircle, MessageCircle } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";

const sections = [
  { id: "core-principle", title: "The Core Principle" },
  { id: "four-steps", title: "The Four Steps" },
  { id: "why-it-works", title: "Why This Works" },
  { id: "practical-example", title: "Practical Example" },
  { id: "warning-signs", title: "Warning Signs You Don't Understand" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

const FeynmanTechniqueGuide = () => {
  useSEO({
    title: "The Feynman Technique: Learn Anything by Teaching It | System Atlas",
    description: "Master the Feynman Technique to learn complex topics faster. A step-by-step guide to the learning method used by one of history's greatest physicists.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/feynman-technique" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/feynman-technique" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/feynman-technique" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="The Feynman Technique: Learn Anything by Teaching It" description="Master the Feynman Technique to learn complex topics faster." publishedDate="2026-02-08" modifiedDate="2026-02-21" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Learning Guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">The Feynman Technique: Learn Anything by Teaching It</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Named after Nobel Prize-winning physicist Richard Feynman, this technique uses teaching as a tool for learning. If you can't explain something simply, you don't understand it well enough.</p>

          <ArticleTOC sections={sections} />

          <section id="core-principle" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-primary" /> The Core Principle</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">Richard Feynman believed that the best way to learn is to teach. When you attempt to explain a concept in simple terms, you quickly discover gaps in your own understanding.</p>
            <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground text-sm">"If you can't explain it to a six-year-old, you don't understand it yourself."<footer className="text-xs mt-2 not-italic">Often attributed to Albert Einstein</footer></blockquote>
          </section>

          <section id="four-steps" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">The Four Steps</h2>
            <div className="space-y-4">
              {[
                { step: "1", title: "Choose a concept", desc: "Write the name of the concept at the top of a blank page." },
                { step: "2", title: "Teach it to a child", desc: "Write an explanation as if teaching a 12-year-old. Use simple language, avoid jargon." },
                { step: "3", title: "Identify gaps and return to source", desc: "When you get stuck, go back to your source material and study until you can explain it simply." },
                { step: "4", title: "Simplify and use analogies", desc: "Review your explanation and simplify further. Create analogies that connect to familiar ideas." },
              ].map((item) => (
                <div key={item.step} className="p-4 border border-border/60 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm">{item.step}</span>
                    <h3 className="font-medium">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="why-it-works" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-primary" /> Why This Works</h2>
            <ul className="space-y-3">
              {[
                { l: "Exposes illusions of competence:", t: "Reading about something feels like learning, but can you actually explain it?" },
                { l: "Forces active processing:", t: "Teaching requires organizing information and making connections." },
                { l: "Builds transferable understanding:", t: "Simple explanations indicate deep comprehension." },
                { l: "Creates useful study materials:", t: "Your explanations become effective revision notes." },
              ].map(i => (
                <li key={i.l} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span className="text-muted-foreground text-sm"><strong className="text-foreground">{i.l}</strong> {i.t}</span></li>
              ))}
            </ul>
          </section>

          <section id="practical-example" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Practical Example</h2>
            <div className="p-5 bg-muted/30 border border-border/60 rounded-lg">
              <h3 className="font-medium mb-3 text-sm">Topic: Photosynthesis</h3>
              <p className="text-muted-foreground mb-3 text-sm"><strong className="text-foreground">Complex version:</strong> "Photosynthesis is a biochemical process wherein chloroplasts utilize light energy to synthesize glucose through the light-dependent and light-independent reactions."</p>
              <p className="text-muted-foreground text-sm"><strong className="text-foreground">Feynman version:</strong> "Plants make their own food using sunlight. The green parts of plants catch light like tiny solar panels. They use this energy to turn water from the soil and carbon dioxide from the air into sugar."</p>
            </div>
            <p className="text-muted-foreground mt-4 leading-relaxed text-sm">Prefer a guided version? The <Link to="/learn/feynman-ai" className="text-primary underline underline-offset-2 hover:opacity-80">Feynman technique with AI</Link> uses an AI as your "student" – exposing weak spots faster than teaching a real person – and pairs well with a <Link to="/learn/perfect-revision-sheet" className="text-primary underline underline-offset-2 hover:opacity-80">well-built revision sheet</Link>.</p>
          </section>

          <section id="warning-signs" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Warning Signs You Don't Understand</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border border-destructive/30 bg-destructive/5 rounded-lg">
                <ul className="text-xs text-muted-foreground space-y-2"><li>• Relying on technical jargon</li><li>• Unable to give a concrete example</li><li>• Explanation sounds like the textbook</li></ul>
              </div>
              <div className="p-4 border border-destructive/30 bg-destructive/5 rounded-lg">
                <ul className="text-xs text-muted-foreground space-y-2"><li>• Can't answer "why" questions</li><li>• Skipping parts of the explanation</li><li>• Feeling frustrated or stuck</li></ul>
              </div>
            </div>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Teaching is the most effective form of learning</li>
              <li>• Simple explanations require deep understanding</li>
              <li>• Jargon often masks incomplete knowledge</li>
              <li>• Analogies help connect new concepts to existing knowledge</li>
              <li>• The goal isn't simplicity for its own sake. it's clarity</li>
              <li>• Return to source material whenever you get stuck</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Learning Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/active-recall" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Active Recall Explained</h4><p className="text-xs text-muted-foreground">The science behind effective learning</p></Link>
            <Link to="/learn/revision-sheets" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Creating Effective Revision Sheets</h4><p className="text-xs text-muted-foreground">Structure your notes for maximum retention</p></Link>
            <Link to="/learn/feynman-ai" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Feynman Technique with AI</h4><p className="text-xs text-muted-foreground">Use AI as a teaching mirror</p></Link>
            <Link to="/learn/atlas-vs-chatgpt-study" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Atlas vs ChatGPT for Studying</h4><p className="text-xs text-muted-foreground">Which AI actually helps you learn</p></Link>
            <Link to="/learn/ai-summarize-lectures" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Summarize Lectures with AI</h4><p className="text-xs text-muted-foreground">From dense PDFs to clear summaries</p></Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default FeynmanTechniqueGuide;
